import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Container, IconButton, InputAdornment,
    Paper, Snackbar, Stack, TextField, Tooltip, Typography
} from '@mui/material';
import {
    Add, Checkroom, DeleteOutline, EditOutlined, Inventory2Outlined, PhotoLibraryOutlined,
    Search, Straighten, WarningAmber
} from '@mui/icons-material';
import AddProductDialog from './dialog/AddProductDialog';
import AddMediaDialog from './dialog/AddMediaDialog';
import AddPositionDialog from './dialog/AddPositionDialog';
import EditProductDialog from './dialog/EditProductDialog';
import { Product } from '../../types/products/interfaces';
import ProductAndCategoryService from '../../logic/ProductAndCategoryService';

const statusLabels: Record<string, { label: string; color: 'success' | 'warning' | 'default' }> = {
    AVAILABLE: { label: 'Доступно', color: 'success' },
    IN_RENT: { label: 'В аренде', color: 'warning' },
    SERVICE: { label: 'Обслуживание', color: 'default' },
};

const ProductComponent: React.FC = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [mediaProduct, setMediaProduct] = useState<Product | null>(null);
    const [positionProduct, setPositionProduct] = useState<Product | null>(null);
    const [editProduct, setEditProduct] = useState<Product | null>(null);
    const [notice, setNotice] = useState<{ text: string; error?: boolean } | null>(null);

    const fetchProducts = async () => {
        setLoading(true);
        try { setProducts(await ProductAndCategoryService.getAllProductsForAdmin()); }
        catch { setNotice({ text: 'Не удалось загрузить каталог.', error: true }); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchProducts(); }, []);

    const filtered = useMemo(() => {
        const normalized = query.trim().toLocaleLowerCase('ru');
        if (!normalized) return products;
        return products.filter(product => [product.name, product.id,
            ...product.categories.flatMap(category => [category.name, category.value])]
            .some(value => value.toLocaleLowerCase('ru').includes(normalized)));
    }, [products, query]);

    const totalPositions = products.reduce((sum, product) => sum + product.positions.length, 0);
    const availablePositions = products.reduce((sum, product) => sum
        + product.positions.filter(position => !position.status || position.status === 'AVAILABLE').length, 0);

    const run = async (action: () => Promise<boolean | void>, successText: string) => {
        try {
            const result = await action();
            if (result === false) throw new Error();
            await fetchProducts();
            setNotice({ text: successText });
        } catch { setNotice({ text: 'Операция не выполнена. Проверьте данные и повторите.', error: true }); }
    };

    return (
        <Container maxWidth="xl" disableGutters>
            <Stack component="header" direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={3} mb={4}>
                <Box sx={{ maxWidth: 700 }}>
                    <Chip icon={<Checkroom />} label="Каталог аренды" size="small" sx={{ mb: 2, borderRadius: 1 }} />
                    <Typography variant="h4" gutterBottom>Товары и позиции</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: 16, lineHeight: 1.65 }}>
                        Управляйте карточками, характеристиками и каждой физической единицей товара из одного экрана.
                    </Typography>
                </Box>
                <Button variant="contained" color="secondary" startIcon={<Add />} onClick={() => setDialogOpen(true)}
                    sx={{ alignSelf: { md: 'flex-start' }, px: 2.5 }}>Добавить товар</Button>
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5, mb: 3 }}>
                {[
                    { label: 'Карточек товара', value: products.length, icon: <Checkroom /> },
                    { label: 'Физических позиций', value: totalPositions, icon: <Inventory2Outlined /> },
                    { label: 'Сейчас доступно', value: availablePositions, icon: <Straighten /> },
                ].map(metric => <Paper key={metric.label} elevation={0} sx={{ p: 2.25, borderRadius: 2.5,
                    display: 'flex', alignItems: 'center', gap: 2, border: '1px solid rgba(24,49,83,.1)' }}>
                    <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center',
                        bgcolor: 'background.alternate', color: 'primary.main' }}>{metric.icon}</Box>
                    <Box><Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>{metric.value}</Typography>
                        <Typography variant="body2" color="text.secondary">{metric.label}</Typography></Box>
                </Paper>)}
            </Box>

            <TextField fullWidth placeholder="Поиск по названию, ID или характеристике" value={query}
                onChange={event => setQuery(event.target.value)} sx={{ mb: 2.5, maxWidth: 620 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />

            {loading ? <Paper elevation={0} sx={{ minHeight: 260, display: 'grid', placeItems: 'center', borderRadius: 3 }}>
                <CircularProgress color="secondary" /></Paper> : filtered.length === 0 ?
                <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                    <WarningAmber color="secondary" sx={{ fontSize: 42, mb: 1 }} />
                    <Typography variant="h6">Товары не найдены</Typography>
                    <Typography color="text.secondary">Измените запрос или добавьте первую карточку товара.</Typography>
                </Paper> :
                <Stack spacing={1.5}>
                    {filtered.map(product => (
                        <Paper key={product.id} component="article" elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3,
                            border: '1px solid rgba(24,49,83,.1)', transition: 'transform 180ms ease, box-shadow 180ms ease',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 18px 45px rgba(24,49,83,.09)' } }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) auto' }, gap: 2 }}>
                                <Box>
                                    <Stack direction="row" alignItems="baseline" gap={1.5} flexWrap="wrap">
                                        <Typography variant="h6" fontWeight={750}>{product.name}</Typography>
                                        <Typography color="secondary.main" fontWeight={750} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                            {new Intl.NumberFormat('ru-RU').format(product.price)} ₽
                                        </Typography>
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{product.id}</Typography>
                                    <Stack direction="row" gap={.75} flexWrap="wrap" mt={1.5}>
                                        {product.categories.map(category => <Chip key={`${category.name}-${category.value}`}
                                            label={`${category.name}: ${category.value}`} size="small" variant="outlined" />)}
                                    </Stack>
                                    <Stack direction="row" gap={1} flexWrap="wrap" mt={2}>
                                        {product.positions.length ? product.positions.map(position => {
                                            const status = statusLabels[position.status || 'AVAILABLE'] || statusLabels.SERVICE;
                                            return <Chip key={position.positionId} label={`${position.size} · ${status.label}`}
                                                size="small" color={status.color} onDelete={() => run(() =>
                                                    ProductAndCategoryService.deleteProductPosition(product.id, position.positionId), 'Позиция удалена.')} />;
                                        }) : <Typography variant="body2" color="text.secondary">Позиции ещё не добавлены</Typography>}
                                    </Stack>
                                </Box>
                                <Stack direction="row" alignItems="flex-start" justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
                                    <Tooltip title="Добавить позицию"><IconButton onClick={() => setPositionProduct(product)}><Add /></IconButton></Tooltip>
                                    <Tooltip title="Медиа"><IconButton onClick={() => setMediaProduct(product)}><PhotoLibraryOutlined /></IconButton></Tooltip>
                                    <Tooltip title="Редактировать"><IconButton onClick={() => setEditProduct(product)}><EditOutlined /></IconButton></Tooltip>
                                    <Tooltip title="Удалить товар"><IconButton color="error" onClick={() => run(() =>
                                        ProductAndCategoryService.deleteProduct(product.id), 'Товар удалён.')}><DeleteOutline /></IconButton></Tooltip>
                                </Stack>
                            </Box>
                        </Paper>
                    ))}
                </Stack>}

            <AddProductDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onAddProduct={() => {
                setDialogOpen(false); fetchProducts(); setNotice({ text: 'Товар добавлен.' });
            }} />
            <EditProductDialog open={Boolean(editProduct)} product={editProduct} onClose={() => setEditProduct(null)}
                onSave={product => run(() => ProductAndCategoryService.updateProduct(product), 'Товар обновлён.')} />
            {mediaProduct && <AddMediaDialog open productId={mediaProduct.id} existingMediaKeys={mediaProduct.mediaObjectKeys || []}
                onClose={() => setMediaProduct(null)} updateProductMedia={async (...args) => {
                    await ProductAndCategoryService.updateProductMedia(...args); await fetchProducts(); setMediaProduct(null);
                }} />}
            {positionProduct && <AddPositionDialog open productId={positionProduct.id} onClose={() => setPositionProduct(null)}
                onAddPosition={async (size, quantity) => { for (let i = 0; i < quantity; i++)
                    await ProductAndCategoryService.addProductPosition(positionProduct.id, size);
                    await fetchProducts(); setPositionProduct(null); setNotice({ text: 'Позиции добавлены.' }); }} />}
            <Snackbar open={Boolean(notice)} autoHideDuration={4000} onClose={() => setNotice(null)}>
                <Alert severity={notice?.error ? 'error' : 'success'} onClose={() => setNotice(null)}>{notice?.text}</Alert>
            </Snackbar>
        </Container>
    );
};

export default ProductComponent;
